/**
 * ImportSheetDialog — Phase H (2026-09-21).
 *
 * Workflow PRINCIPAL de création/enrichissement de fiches pratiques :
 * import JSON via (A) collage, (B) fichier .json, (C) lot (tableau de fiches).
 *
 * RÉUTILISE STRICTEMENT le moteur backend Phase G (POST /admin/practical-sheets/import)
 * avec `dry_run=true` pour PRÉVISUALISER avant toute écriture DB.
 *
 * Règles backend (vérifiées) : BROUILLON uniquement, jamais publié/validé/testé
 * automatiquement ; fiche existante → ENRICHI ; ambiguïté → validation humaine ;
 * dossier vivant préservé. Rapport fiche par fiche :
 *   - created_draft | enriched_existing | ambiguous_needs_human | skipped_no_identifier
 */
import { useState, useMemo } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Upload, FileJson, Eye, Download, Copy, Loader2, FileUp, AlertTriangle,
  CheckCircle2, PlusCircle, Boxes, FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Modèle JSON conforme EXACTEMENT à `ImportPayload` du backend Phase G1.
// Un seul objet pour mode SIMPLE (A/B), un tableau `sheets` pour LOT.
const MODEL_SINGLE = {
  sheets: [
    {
      title: "Marseille (exemple)",
      port_name: "Marseille",
      aliases: ["Marseille (France)"],
      payload: {
        country: "France",
        destination_id: null,
        short_intro: "Première ville portuaire de France, cosmopolite et solaire.",
        port_arrival_info: "Terminal MPCT (Môle Léon Gourret), parfois Môle A/B.",
        distance_port_to_center: "~3 km du Vieux-Port",
        transport_options: [
          { type: "shuttle", duration: "15 min", price_indicative: "8 €", notes: "Navette compagnie" },
          { type: "taxi", duration: "10 min", price_indicative: "15–20 €", notes: null },
        ],
        what_to_do_4h: "Vieux-Port, Panier, Cathédrale de la Major, Mucem.",
        highlights: "Notre-Dame-de-la-Garde, Calanques, bouillabaisse.",
        avoid_mistakes: "Éviter le quartier gare à pied tard le soir.",
        source: "Office de tourisme Marseille",
        source_url: "https://www.marseille-tourisme.com/",
        last_verified_at: "2026-09-21",
        freshness_status: "to_verify",
      },
    },
  ],
  itineraries: [],
  default_source: null,
  default_source_url: null,
};

const MODEL_BATCH = {
  sheets: [
    { title: "Fiche A", port_name: "A", aliases: [], payload: { country: "France" } },
    { title: "Fiche B", port_name: "B", aliases: [], payload: { country: "Italie" } },
  ],
  itineraries: [],
};

function parseJsonSafe(text) {
  try {
    return { ok: true, data: JSON.parse(text) };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}

function PreviewReport({ report, isDry }) {
  if (!report) return null;
  const c = report.counts || {};
  return (
    <div className="rounded-md border border-border p-3 bg-muted/30 space-y-2" data-testid="import-preview-report">
      <div className="text-xs flex flex-wrap gap-x-4 gap-y-1">
        <span><strong>Entrées :</strong> {c.sheets_input ?? 0}</span>
        <span className="text-emerald-700"><PlusCircle className="inline h-3 w-3 mr-0.5" /> Nouvelles : <strong>{c.sheets_created ?? 0}</strong></span>
        <span className="text-sky-700"><FileUp className="inline h-3 w-3 mr-0.5" /> Enrichies : <strong>{c.sheets_enriched ?? 0}</strong></span>
        <span className="text-amber-700"><AlertTriangle className="inline h-3 w-3 mr-0.5" /> Ambiguës : <strong>{c.sheets_ambiguous ?? 0}</strong></span>
        {isDry && <span className="text-[10px] uppercase bg-amber-100 text-amber-900 px-2 py-0.5 rounded">Prévisualisation — aucune écriture DB</span>}
      </div>
      <ul className="divide-y divide-border max-h-60 overflow-y-auto text-sm" data-testid="import-report-items">
        {(report.sheets || []).map((r, i) => (
          <li key={i} className="py-2 flex items-start gap-2" data-testid={`import-report-item-${i}`}>
            {r.action === "created_draft" && <PlusCircle className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
            {r.action === "enriched_existing" && <FileUp className="h-4 w-4 text-sky-600 shrink-0 mt-0.5" />}
            {r.action === "ambiguous_needs_human" && <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />}
            {r.action === "skipped_no_identifier" && <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs">
                <span className="font-medium">#{r.index + 1} · {r.title || <em>sans titre</em>}</span>
                {r.slug && <span className="text-muted-foreground"> — {r.slug}</span>}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {r.action === "created_draft" && "Nouvelle fiche BROUILLON (jamais publiée auto)"}
                {r.action === "enriched_existing" && `Fiche existante enrichie — champs : ${(r.fields_enriched || []).join(", ") || "aucun ajout"}`}
                {r.action === "ambiguous_needs_human" && `Correspondance ambiguë (${(r.candidates || []).length} fiches) → validation humaine requise, aucune fusion`}
                {r.action === "skipped_no_identifier" && "Ignorée (aucun titre/port_name/alias)"}
              </div>
              {Array.isArray(r.warnings) && r.warnings.length > 0 && (
                <div className="text-[11px] text-amber-700">Alertes : {r.warnings.join(", ")}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ImportSheetDialog({ open, onOpenChange, onImportDone }) {
  const [mode, setMode] = useState("paste"); // paste | file | batch
  const [pasteText, setPasteText] = useState("");
  const [fileText, setFileText] = useState("");
  const [fileName, setFileName] = useState("");
  const [batchText, setBatchText] = useState("");
  const [previewReport, setPreviewReport] = useState(null);
  const [previewDry, setPreviewDry] = useState(false);
  const [loading, setLoading] = useState(false);

  const currentText = mode === "paste" ? pasteText : mode === "file" ? fileText : batchText;

  const parsed = useMemo(() => {
    const txt = currentText.trim();
    if (!txt) return { ok: false, error: null };
    return parseJsonSafe(txt);
  }, [currentText]);

  const callImport = async (dry) => {
    if (!parsed.ok) {
      toast.error("JSON invalide : " + (parsed.error || "format non reconnu"));
      return;
    }
    let payload = parsed.data;
    // Si l'utilisateur colle directement {title: "..."} (un objet seul), on
    // l'enveloppe en {sheets: [objet], ...}.
    if (payload && !payload.sheets && !payload.itineraries && (payload.title || payload.port_name)) {
      payload = { sheets: [{ title: payload.title, port_name: payload.port_name, aliases: payload.aliases || [], payload: payload.payload || {} }] };
    }
    if (!payload.sheets && !payload.itineraries) {
      toast.error("Format inattendu : attend `sheets: [...]` (voir modèle).");
      return;
    }
    payload.dry_run = !!dry;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/practical-sheets/import`, payload);
      setPreviewReport(r.data);
      setPreviewDry(!!dry);
      if (dry) {
        toast.success("Prévisualisation prête — vérifiez puis cliquez sur Importer.");
      } else {
        const c = r.data.counts || {};
        toast.success(`Import terminé : ${c.sheets_created || 0} nouvelle(s), ${c.sheets_enriched || 0} enrichie(s), ${c.sheets_ambiguous || 0} ambiguë(s).`);
        if (typeof onImportDone === "function") onImportDone(r.data);
      }
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e?.message || "inconnue"));
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => setFileText(String(ev.target?.result || ""));
    reader.onerror = () => toast.error("Lecture fichier impossible.");
    reader.readAsText(f);
  };

  const copyModel = async (modelObj) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(modelObj, null, 2));
      toast.success("Modèle JSON copié dans le presse-papier.");
    } catch {
      toast.error("Impossible de copier (navigateur).");
    }
  };

  const downloadModel = (modelObj, name) => {
    const blob = new Blob([JSON.stringify(modelObj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="import-sheet-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" /> Importer une fiche (JSON)
          </DialogTitle>
          <DialogDescription>
            Workflow principal : importez un JSON préparé hors E3. Les fiches existantes sont ENRICHIES (jamais écrasées) ; les nouvelles arrivent en BROUILLON. Jamais de publication automatique.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mb-2">
          <Button variant="ghost" size="sm" onClick={() => copyModel(MODEL_SINGLE)} data-testid="copy-model-btn">
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copier le modèle JSON
          </Button>
          <Button variant="ghost" size="sm" onClick={() => downloadModel(MODEL_SINGLE, "itercatus-fiche-modele.json")} data-testid="download-model-btn">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Télécharger le modèle
          </Button>
          <Button variant="ghost" size="sm" onClick={() => copyModel(MODEL_BATCH)} data-testid="copy-model-batch-btn">
            <Boxes className="h-3.5 w-3.5 mr-1.5" /> Modèle LOT
          </Button>
        </div>

        <Tabs value={mode} onValueChange={(v) => { setMode(v); setPreviewReport(null); }}>
          <TabsList data-testid="import-tabs">
            <TabsTrigger value="paste"><FileText className="h-3.5 w-3.5 mr-1.5" /> Coller</TabsTrigger>
            <TabsTrigger value="file"><FileJson className="h-3.5 w-3.5 mr-1.5" /> Fichier .json</TabsTrigger>
            <TabsTrigger value="batch"><Boxes className="h-3.5 w-3.5 mr-1.5" /> Lot</TabsTrigger>
          </TabsList>

          <TabsContent value="paste" className="mt-3 space-y-2">
            <label className="text-sm font-medium">Coller le JSON ici</label>
            <Textarea
              value={pasteText}
              onChange={(e) => { setPasteText(e.target.value); setPreviewReport(null); }}
              rows={12}
              placeholder='{"sheets": [{"title": "...", "port_name": "...", "aliases": ["..."], "payload": {...}}]}'
              className="font-mono text-xs"
              data-testid="import-paste-textarea"
            />
            {!parsed.ok && pasteText.trim() && (
              <div className="text-xs text-rose-700">JSON invalide : {parsed.error}</div>
            )}
          </TabsContent>

          <TabsContent value="file" className="mt-3 space-y-2">
            <label className="text-sm font-medium">Sélectionner un fichier .json</label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={onFileChange}
              className="block text-sm"
              data-testid="import-file-input"
            />
            {fileName && (
              <div className="text-xs text-muted-foreground">Fichier chargé : <strong>{fileName}</strong></div>
            )}
            {fileText && (
              <Textarea
                value={fileText}
                readOnly
                rows={8}
                className="font-mono text-xs"
                data-testid="import-file-preview"
              />
            )}
            {!parsed.ok && fileText && (
              <div className="text-xs text-rose-700">JSON invalide : {parsed.error}</div>
            )}
          </TabsContent>

          <TabsContent value="batch" className="mt-3 space-y-2">
            <div className="text-xs text-muted-foreground">
              Collez un objet contenant <code className="font-mono">sheets: [...]</code> avec plusieurs fiches.
              Le rapport sera fiche par fiche (nouvelle/enrichissement/ambiguïté/erreur).
            </div>
            <Textarea
              value={batchText}
              onChange={(e) => { setBatchText(e.target.value); setPreviewReport(null); }}
              rows={14}
              placeholder='{"sheets": [ { ... }, { ... } ]}'
              className="font-mono text-xs"
              data-testid="import-batch-textarea"
            />
            {!parsed.ok && batchText.trim() && (
              <div className="text-xs text-rose-700">JSON invalide : {parsed.error}</div>
            )}
          </TabsContent>
        </Tabs>

        {previewReport && <div className="mt-3"><PreviewReport report={previewReport} isDry={previewDry} /></div>}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button
            variant="outline"
            onClick={() => callImport(true)}
            disabled={!parsed.ok || loading}
            data-testid="import-preview-btn"
          >
            {loading && previewDry ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Prévisualiser
          </Button>
          <Button
            onClick={() => callImport(false)}
            disabled={!parsed.ok || loading}
            data-testid="import-confirm-btn"
          >
            {loading && !previewDry ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Importer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
